import { icons } from '@portal/components'
import React from 'react'

import { useDQLEditorContext } from '../dqlEditor/context'
import EmptyResultTips from './EmptyResultTips'
const { Inbox, ExclamationCircle } = icons

/**
 * A component that renders the corresponding UI messaging when there are no results to display.
 * If there are no results due to an error, the error message will be displayed instead.
 */
export default function NoResults() {
  const { activeView, appCollections, result, errorMessage } =
    useDQLEditorContext()

  const queryExists = !!result
  const isCollectionsView = activeView === 'collections'
  const hasCollections = appCollections && appCollections.length > 0

  // Sets the message title and body depending on the active view and
  // whether or not the app has any collections
  const messageTitle =
    isCollectionsView && !hasCollections ? 'No collections' : 'No results'
  const messageBody = isCollectionsView
    ? hasCollections
      ? 'No documents found in collection'
      : 'Create a collection to get started'
    : 'Run a query to get started'

  const renderContent = () => {
    if (errorMessage) {
      return (
        <div className="flex flex-col items-center justify-center gap-y-1">
          <ExclamationCircle className="h-10 w-10 text-red-800" />
          <h3 className="font-medium text-foreground-normal">
            Failed to run DQL query
          </h3>
          <p className="text-foreground-subtle">{errorMessage}</p>
        </div>
      )
    }

    const { queryType } = result || {}

    // We won't have a query type if there is no response, and queryType will
    // only be 'unknown' if there was an error present. So we can assume at this
    // point it is either a 'select' or 'mutation' that returned no results
    const attemptMessage =
      queryType === 'mutation'
        ? 'No changes were detected'
        : 'No results were returned'

    // Whether or not the query exists and the active view is the editor. This
    // is used to determine whether or not to show the attempt message or the
    // collection message, plus the empty result tips
    const queryExistsAndIsEditorView = queryExists && !isCollectionsView

    return (
      <>
        <div className="flex w-full flex-col items-center justify-center gap-y-1">
          <Inbox className="h-10 w-10 text-foreground-subtle" />
          <h3 className="font-medium dark:text-gray-100">
            {queryExistsAndIsEditorView ? attemptMessage : messageTitle}
          </h3>
          <p className="text-foreground-subtle">
            {queryExistsAndIsEditorView ? 'Try a different query' : messageBody}
          </p>
        </div>

        {queryExistsAndIsEditorView && <EmptyResultTips />}
      </>
    )
  }

  return (
    <div
      className="flex h-full w-full flex-col items-center justify-center gap-y-8"
      data-testid="noResults"
    >
      {renderContent()}
    </div>
  )
}
